import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, ShoppingCart, TrendingUp, Package, BarChart3, Globe, Clock, Award, Search, Truck } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { EnhancedStructuredData } from "@/components/EnhancedStructuredData";
import { SEOArticleStructure } from "@/components/SEOArticleStructure";
import { useSEOOptimization } from "@/hooks/useSEOOptimization";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const EcommerceMarketplacesTheWoodlandsPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  
  useSEOOptimization();
  const canonicalUrl = generateCanonicalUrl('/ecommerce-marketplaces-the-woodlands');

  const services = [
    {
      icon: Search,
      title: "Auditoría de Tienda Online",
      description: "Revisión de UX, velocidad, catálogo, precios, competencia y logística."
    },
    {
      icon: Package,
      title: "Optimización de Listings",
      description: "Títulos, descripciones, keywords, imágenes, reviews y categorías."
    },
    {
      icon: ShoppingCart,
      title: "Campañas Marketplace",
      description: "Amazon, eBay: Sponsored Products, reviews, posicionamiento competitivo."
    },
    {
      icon: TrendingUp,
      title: "Tienda Propia + Remarketing",
      description: "Shopify, WooCommerce con remarketing dinámico y embudos avanzados."
    },
    {
      icon: Globe,
      title: "Internacionalización Bilingüe",
      description: "Contenido ES/EN, localización y logística para mercados hispanos."
    },
    {
      icon: BarChart3,
      title: "Tracking & Analítica de Ventas",
      description: "GMV, ROAS, CAC, margen y ciclo de vida del cliente."
    }
  ];

  const process = [
    {
      day: "Día 0-14",
      title: "Diagnóstico Completo",
      description: "Revisión de catálogo, competencia, plataformas y métricas base."
    },
    {
      day: "Día 15-45",
      title: "Optimización & Configuración",
      description: "Listings optimizados, campañas marketplace y remarketing dinámico."
    },
    {
      day: "Día 46-75",
      title: "Escalado Internacional",
      description: "Pruebas A/B, expansión de productos, segmentación bilingüe y logística."
    },
    {
      day: "Día 76-90",
      title: "Análisis & Crecimiento",
      description: "Resultados, ajustes estratégicos y plan de expansión a nuevos canales."
    }
  ];

  const results = [
    "Incremento en ventas y GMV mensuales con reducción de CAC",
    "Mejora significativa en visibilidad y ranking de productos en marketplaces",
    "Aumento de conversión en tienda propia mediante remarketing y UX optimizada",
    "Expansión de audiencia bilingüe (ES/EN) y acceso a nuevos mercados"
  ];

  const sectors = [
    "Productos de Consumo",
    "Retail Especializado",
    "Marcas D2C Bilingües",
    "Servicios con Productos Complementarios",
    "Tiendas Locales que Escalan Online"
  ];

  const plans = [
    {
      name: "Starter Ecommerce",
      duration: "Setup inicial",
      description: "Base sólida para comenzar a optimizar tu presencia en marketplaces.",
      features: [
        "Auditoría completa",
        "10 listados optimizados",
        "Remarketing inicial",
        "Informe de competencia"
      ]
    },
    {
      name: "Growth Ecommerce",
      duration: "Mensual",
      description: "Escalado estratégico con múltiples canales y analítica profesional.",
      features: [
        "50 listados optimizados",
        "Campañas marketplace",
        "Tienda propia optimizada",
        "Analítica básica"
      ]
    },
    {
      name: "Marketplace Suite Premium",
      duration: "Mensual",
      description: "Solución completa para dominar marketplaces y escalar internacionalmente.",
      features: [
        "Catálogo completo",
        "Dominio bilingüe ES/EN",
        "Expansión internacional",
        "Análisis continuo + mantenimiento"
      ]
    }
  ];

  const faqs = [
    {
      question: "¿En qué marketplaces trabajamos?",
      answer: "Trabajamos con Amazon USA, eBay, Shopify, WooCommerce y otros canales relevantes según tu industria y objetivos de mercado."
    },
    {
      question: "¿Cuánto tiempo para ver ventas?",
      answer: "Dependiendo del catálogo, competitividad y canal — típicamente 30-60 días para mejoras visibles en métricas, con escalado consolidado a los 90+ días."
    },
    {
      question: "¿Necesito tener ya una tienda online?",
      answer: "No necesariamente. Podemos optimizar tu tienda existente o crear una nueva tienda rápidamente para escalar ventas según tus necesidades."
    },
    {
      question: "¿Cómo medimos el ROI o GMV?",
      answer: "Utilizamos dashboards de ventas, analítica de plataforma + campañas, seguimiento del CAC, ROAS, margen por producto y ciclo de vida completo del cliente."
    }
  ];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Marketing Ecommerce & Marketplaces en The Woodlands | Escala tus ventas – Luis ROC</title>
        <meta 
          name="description" 
          content="Impulsa tu tienda online y vende en Amazon, Shopify o eBay desde The Woodlands. Estrategia bilingüe de marketplace + e-commerce con Luis ROC — GMV que crece." 
        />
        <meta name="keywords" content="marketing ecommerce the woodlands, amazon marketing, shopify optimization, marketplace strategy, ecommerce bilingüe, ventas online" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content="Marketing Ecommerce & Marketplaces The Woodlands | Luis ROC" />
        <meta property="og:description" content="Estrategia bilingüe para escalar ventas en marketplaces y tiendas online." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Marketing Ecommerce & Marketplaces | Luis ROC" />
        <meta name="twitter:description" content="Impulsa tus ventas online en Amazon, Shopify y más." />
      </Helmet>

      <EnhancedStructuredData
        service={{
          name: "Marketing Ecommerce & Marketplaces The Woodlands",
          description: "Servicios profesionales de marketing para tiendas online y marketplaces en The Woodlands, Texas",
          provider: "Luis ROC",
          areaServed: ["The Woodlands", "Houston", "Spring", "Conroe", "Montgomery County", "Texas"],
        }}
        faqs={faqs}
        webpage={{
          name: "Marketing Ecommerce & Marketplaces en The Woodlands",
          description: "Servicios profesionales de marketing ecommerce y marketplaces en The Woodlands, TX. Escala tus ventas online.",
          url: canonicalUrl,
          keywords: ["marketing ecommerce", "amazon marketing", "shopify optimization", "marketplace strategy"]
        }}
      />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main>
          <SEOArticleStructure
            title="Marketing Ecommerce & Marketplaces en The Woodlands que dispara tus ventas"
            description="Servicios profesionales de marketing para tiendas online y marketplaces. Estrategia bilingüe para escalar GMV."
            author="Luis ROC"
            keywords={["marketing ecommerce", "amazon marketing", "marketplace strategy"]}
            className="container mx-auto px-4 py-8"
          >
            {/* Breadcrumb Navigation */}
            <BreadcrumbNav 
              items={[
                { name: "Servicios", url: "/servicios" },
                { name: "Ecommerce & Marketplaces", url: "/ecommerce-marketplaces-the-woodlands" }
              ]}
              language="es"
            />

            {/* Hero Section */}
            <section className="py-16 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                Marketing Ecommerce & Marketplaces en The Woodlands que dispara tus ventas
              </h1>
              
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  En Luis ROC apoyamos a negocios desde The Woodlands a expandir sus ventas online, ya sea mediante tu tienda 
                  propia (Shopify, WooCommerce) o marketplaces como Amazon y eBay. Combinamos optimización de listing, feed de 
                  productos, remarketing dinámico, análisis de datos y estrategia bilingüe (ES/EN) para que cada clic se convierta 
                  en compra. Nos enfocamos tanto en mercado local/hispano como en expansión nacional o internacional. Si tu objetivo 
                  es no solo tráfico, sino escalar ventas, mejorar márgenes y dominar marketplaces, estás en el lugar correcto.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => window.location.href = '/contacto'}
                  className="text-lg px-8"
                >
                  Agenda tu reunión inicial gratuita de 30 minutos
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.location.href = '/casos-de-exito'}
                  className="text-lg px-8"
                >
                  Ver Casos de Éxito
                </Button>
              </div>
            </section>

            {/* Services Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Nuestros servicios de Ecommerce & Marketplaces
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                        <Icon className="h-12 w-12 text-primary mb-4" />
                        <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                        <p className="text-muted-foreground">{service.description}</p>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Process Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Nuestro proceso (90 días)
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Entregamos panel de control de ventas, informe de métricas y plan trimestral
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {process.map((step, index) => (
                  <Card key={index} className="p-6 relative">
                    <div className="absolute -top-4 left-6 bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex items-center gap-2 mb-3 mt-2">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-primary">{step.day}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* Results Section */}
            <section className="py-16 bg-primary/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Resultados que puedes esperar
                </h2>
                
                <div className="max-w-3xl mx-auto space-y-4">
                  {results.map((result, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-background rounded-lg">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-lg">{result}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Sectors Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Sectores que servimos
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Especialistas en negocios desde The Woodlands y Texas hispano-inglés
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                {sectors.map((sector, index) => (
                  <div 
                    key={index}
                    className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-medium hover:bg-accent/80 transition-colors"
                  >
                    {sector}
                  </div>
                ))}
              </div>
            </section>

            {/* Plans Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                  Planes y precios
                </h2>
                <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                  Solicita propuesta personalizada según tu volumen de productos / mercados
                </p>
                
                <div className="grid md:grid-cols-3 gap-8">
                  {plans.map((plan, index) => (
                    <Card key={index} className="p-8 hover:shadow-xl transition-shadow">
                      <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                      <p className="text-primary font-semibold mb-4">{plan.duration}</p>
                      <p className="text-muted-foreground mb-6">{plan.description}</p>
                      
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button 
                        className="w-full"
                        onClick={() => window.location.href = '/contacto'}
                      >
                        Solicitar Propuesta
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Preguntas Frecuentes
              </h2>
              
              <div className="max-w-3xl mx-auto space-y-6">
                {faqs.map((faq, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Acelera tus Ventas Online
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                Revisión inicial del contexto de tu tienda o marketplace y definición de posibles siguientes pasos
              </p>
              <Button 
                size="lg"
                variant="secondary"
                onClick={() => window.location.href = '/contacto'}
                className="text-lg px-8"
              >
                Agenda tu reunión inicial gratuita de 30 minutos
              </Button>
            </section>

            {/* Internal Links Section */}
            <section className="py-16">
              <h2 className="text-2xl font-bold mb-6 text-center">Recursos Relacionados</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">SEO The Woodlands</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Combina ecommerce con SEO para tráfico orgánico sostenible
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/seo-the-woodlands'}>
                    Ver SEO Local →
                  </Button>
                </Card>
                
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">Google Ads The Woodlands</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Acelera ventas con campañas PPC para productos
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/google-ads-the-woodlands'}>
                    Ver PPC →
                  </Button>
                </Card>
                
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">Casos de Éxito</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Resultados reales de tiendas que escalaron ventas
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/casos-de-exito'}>
                    Ver casos →
                  </Button>
                </Card>
              </div>
            </section>
          </SEOArticleStructure>
        </main>

        <KnowledgeConnections path="/ecommerce-marketplaces-the-woodlands" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default EcommerceMarketplacesTheWoodlandsPage;
